import { computed, readonly, ref } from 'vue';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { useUserAgent } from '@/helpers/composables/useUserAgent';

import { toValue, type MaybeRefOrGetter } from '@vueuse/core';

import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { Route } from 'vue-router';
import { Maybe } from '@/helpers/types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { ClickoutUrl } from '@/helpers/clickout-helper';

const sessionStorage = process.client && window != undefined ? window.sessionStorage : null;

// prettier-ignore
const ArbitrationCountries = [
	'AR', 'AT', 'AU', 'BE', 'BR', 'CA', 'CH', 'CL', 'CO', 'CZ', 'DE', 'DK', 'ES', 'FI', 'FR', 'GB', 'IE', 'IL', 'IN', 'IT', 'MX', 'NL', 'NO', 'NZ', 'PH', 'PL', 'PT', 'SA', 'SE', 'US', 'ZA',
];

const EMPTY = '$' as const;

/*
**Note aboute utm_network & gclid**

`utm_network` and `gclid` are directly related to each other and `gclid` needs to be present when `utm_network` is.
Snowplow depends on `gclid` to derive the proper `utm_network`.
More info: https://docs.snowplow.io/docs/enriching-your-data/available-enrichments/campaign-attribution-enrichment/
*/
const utmParams = ['utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'utm_network', 'gclid'] as const;

export type UtmArbitrationValues = { [param in (typeof utmParams)[number]]: string };
interface ArbitrationValues extends UtmArbitrationValues {
	titleid: string;
	device: 'm' | 'd';
}

const _isArbitrating = ref(false);
export const isArbitrating = readonly(_isArbitrating);

const ArbitrageProviders = [
	'atp', // Apple TV+
	'itu', // iTunes
	'hlu', // Hulu
	'amp', // Amazon Prime Video
];

/** Call as early as possible to check if the current session is paid (has utm_*). */
export function initArbitrage(route: Route, country: string) {
	if (!ArbitrationCountries.includes(country)) return;

	if (_isArbitrating.value || process.server) return;

	type UTMQueryParams = [string, string | null][];
	const utmValues = utmParams
		.map(param => [param, route.query[param] as string | null])
		.filter(([, value]) => value != null) as UTMQueryParams;
	if (utmValues.length === 0) return;

	utmValues.forEach(([key, value]) => sessionStorage?.setItem(key, value!));

	_isArbitrating.value = true;
}

interface UseArbitrageOptions {
	entityId?: MaybeRefOrGetter<Maybe<string>>;
}

export function useArbitrage(options: UseArbitrageOptions = {}) {
	const { country } = useLanguageStore();

	const clearArbitration = () => {
		utmParams.forEach(param => sessionStorage?.removeItem(param));

		_isArbitrating.value = false;
	};

	const { deviceInfo, deviceType } = useUserAgent();

	const arbitration = computed<ArbitrationValues | null>(() => {
		if (!isArbitrating.value) return null;

		const {
			utm_medium = EMPTY,
			utm_content = EMPTY,
			utm_campaign = EMPTY,
			utm_source = EMPTY,
			utm_network = EMPTY,
			gclid = EMPTY,
		} = getSessionUtmQueryParams()!;
		const titleid = toValue(options.entityId) ?? EMPTY;
		const device = deviceType.value === 'mobile' ? 'm' : 'd';

		return { utm_medium, titleid, device, utm_content, utm_campaign, utm_source, utm_network, gclid };
	});

	/** Sets the 'uct_ct' query on the Offer URL if needed. */
	function withArbitrageTracking({
		offer,
		title,
		placement,
	}: {
		offer: MaybeRefOrGetter<TitleOfferFragment | null>;
		title?: MaybeRefOrGetter<TitleDetail | undefined>;
		placement: 'ft' | 'ro';
	}) {
		const offerValue = toValue(offer);
		const titleValue = toValue(title);
		const shortName = offerValue?.package.shortName ?? '';
		const handler = getArbitrationHandler(shortName);

		if (ArbitrageProviders.includes(shortName) && arbitration.value && handler && offerValue) {
			return handler.plugin(offerValue, arbitration.value, country.value, placement, titleValue);
		}
		return null;
	}

	function trackTagEvent(offer: MaybeRefOrGetter<TitleOfferFragment | null>) {
		if (!window || !window.dataLayer) return;
		if (!isArbitrating.value) return;

		const shortName = toValue(offer)?.package.shortName ?? '';
		const handler = getArbitrationHandler(shortName);

		if (ArbitrageProviders.includes(shortName)) {
			window.dataLayer.push({
				event: 'interaction',
				// event category
				target: 'clickout',
				// event label
				'target-properties': handler?.clearName ?? '',
				device: deviceInfo.value,
			});
		}
	}

	return {
		isArbitrating,
		arbitration,
		clearArbitration,
		withArbitrageTracking,
		trackTagEvent,
	};
}

/** Get the `utm_*` query params as a URLSearchParam object. `null` if not Arbitrating. */
export function getUtmSearchParams() {
	if (isArbitrating.value == null) return null;

	const sessionParams = getSessionUtmQueryParams();
	if (sessionParams == null) return null;

	const paramString = utmParams
		.filter(param => sessionParams[param] != null)
		.map(param => `${param}=${sessionParams[param]}`)
		.join('&');

	return new URLSearchParams(paramString);
}

/** Get the `utm_*` query params from the current session. `null` if not Arbitrating. */
export function getSessionUtmQueryParams() {
	if (isArbitrating.value == null) return null;

	const utm = utmParams.map(param => sessionStorage?.getItem(param) ?? undefined);
	if (utm.every(param => param == null)) return null;

	const [utm_campaign, utm_content, utm_medium, utm_source, utm_network, gclid] = utm;

	return { utm_medium, utm_content, utm_campaign, utm_source, utm_network, gclid };
}

interface ArbitrationHandler {
	clearName: string;
	plugin: (
		offer: TitleOfferFragment,
		values: ArbitrationValues,
		country: string,
		placement: 'ft' | 'ro',
		title?: TitleDetail
	) => ClickoutUrl | null;
}

const prime: ArbitrationHandler = {
	clearName: 'Amazon Prime Video',
	plugin: (offer, { utm_medium, utm_source, utm_campaign }, country, placement, title) => {
		const isUSPrimeArbitration =
			country === 'US' &&
			utm_source === 'facebook' &&
			utm_medium === 'cpc' &&
			utm_campaign === 'expert-unit_jul-2025_catalog_us';

		if (!isUSPrimeArbitration || !title) return null;

		const pageTypeLower = title.objectType.toLowerCase();
		const pageType = pageTypeLower === 'show_season' ? 'sea' : pageTypeLower.slice(0, 3);

		// I don't think this is needed, but we will break affiliation if we pass an invalid tag
		if (!['mov', 'sho', 'sea'].includes(pageType)) return null;

		const tag = `pv-fb-${pageType}-20`;

		const url = new ClickoutUrl(offer).set('uct_country', country).set('uct_ctag', tag);

		return url;
	},
};

const appletv: ArbitrationHandler = {
	clearName: 'Apple TV',
	plugin: (offer, { utm_medium, utm_source, utm_campaign, utm_content }, country, placement) => {
		const isATVArbitration =
			utm_source === 'google' &&
			utm_medium === 'cpc' &&
			// manual campaign - sea_at, dynamic campaign - dsa_at
			['sea_at', 'dsa_at'].includes(utm_campaign) &&
			utm_content === 'tl';

		if (!isATVArbitration) return null;

		const campaign = utm_campaign.slice(0, 3); // sea or dsa

		const ctParam = `apple-${campaign}-${country.toLowerCase()}-${placement}`;

		const url = new ClickoutUrl(offer).set('uct_country', country).set('uct_ctag', ctParam);

		return url;
	},
};

function getArbitrationHandler(shortName: string): ArbitrationHandler | null {
	switch (shortName) {
		case 'amp':
			return prime;
		case 'atp':
			return appletv;
		default:
			return null;
	}
}
